import { useCallback, useEffect, useMemo, useState } from 'react'
import { create, StoreApi, UseBoundStore } from 'zustand'
import { clone } from 'ramda'
import { notification } from 'antd'
import {
  CoreEntity,
  CoreEntityMap,
  CoreEntityType,
  PromptType,
  Response,
  PromptLibraryState,
  DeepPartial,
} from '#/index'
import { databaseManager } from '@/services'
import { structuralUpdate, getNovelActsTree, getCodexEntryGroups } from '@/utils'
import defaultsState from '@/constants/defaults'

const novelCoreDb = (type: CoreEntityType) => {
  switch (type) {
    case CoreEntityType.CodexEntries:
      return databaseManager.codexEntries
    case CoreEntityType.CodexFields:
      return databaseManager.codexFields
    case CoreEntityType.CodexCategories:
      return databaseManager.codexCategories
    case CoreEntityType.Prompts:
      return databaseManager.prompts
    case CoreEntityType.PromptDefaults:
      return databaseManager.promptDefaults
    case CoreEntityType.PromptPersonas:
      return databaseManager.promptPersonas
    case CoreEntityType.PromptPresets:
      return databaseManager.promptPresets
    case CoreEntityType.ModelBanks:
      return databaseManager.modelBanks
    case CoreEntityType.ModelBankLinks:
      return databaseManager.modelBankLinks
    case CoreEntityType.Novels:
      return databaseManager.novels
    case CoreEntityType.NovelScenes:
      return databaseManager.novelScenes
    case CoreEntityType.NovelActs:
      return databaseManager.novelActs
    case CoreEntityType.NovelChapters:
      return databaseManager.novelChapters
    case CoreEntityType.NovelSeries:
      return databaseManager.novelSeries
    case CoreEntityType.Snippets:
      return databaseManager.snippets
    case CoreEntityType.ChatThreads:
      return databaseManager.chatThreads
    case CoreEntityType.ChatMessages:
      return databaseManager.chatMessages
    default:
      return undefined
  }
}


type NovelCoreState = {
  [T in CoreEntityType]: CoreEntityMap<T>[]
} & {
  currentNovelId: string
};

type UpdateCoreEntityParams = { id: string } & DeepPartial<Omit<CoreEntity, 'id'>>;

interface NovelCoreActions {
  addCoreDraftData: (coreEntity: CoreEntity) => void
  addCoreData: (coreEntity: CoreEntity) => Promise<Response<void>>
  // skipDbUpdate 跳过数据库更新
  updateCoreData: (updates: CoreEntity, skipDbUpdate?: boolean) => Promise<Response<void>>
  structuralUpdateCoreData: (updates: UpdateCoreEntityParams, skipDbUpdate?: boolean) => Promise<Response<void>>
  deleteCoreData: (id: string) => Promise<Response<void>>
  getCoreDataById: <T extends CoreEntityType>(id: string, type?: T) => CoreEntityMap<T> | undefined
  getCoreDataByType: <T extends CoreEntityType>(type: T) => CoreEntityMap<T>[]
  getCoreData: () => CoreEntity[]
  initCoreData: () => Promise<Response<void>>
  initNovelDetailsById: (novelId: string) => Promise<Response<void>>
}

export type NovelCoreStore = UseBoundStore<StoreApi<NovelCoreState & NovelCoreActions>>;

export const useNovelCoreStore: NovelCoreStore = create<NovelCoreState & NovelCoreActions>()(
  (set, get) => ({
    prompts: clone(defaultsState.defaultPrompts),
    promptDefaults: [],
    promptPersonas: clone(defaultsState.defaultPromptPersonas as any),
    promptPresets: [],
    modelBanks: clone(defaultsState.defaultModelBanks),
    modelBankLinks: clone(defaultsState.defaultModelBankLinks),
    novels: [],
    novelSeries: [],
    chatThreads: [],

    currentNovelId: '',
    novelScenes: [],
    novelActs: [],
    novelChapters: [],
    snippets: [],
    chatMessages: [],
    codexEntries: [],
    codexFields: [],
    codexCategories: [],

    addCoreDraftData: (coreEntity: CoreEntity) => {
      set((state) => ({
        ...state,
        [coreEntity.type]: [
          ...state[coreEntity.type as CoreEntityType],
          structuralUpdate(coreEntity, { meta: { state: 'draft' } })
        ]
      }))
    },
    addCoreData: (coreEntity: CoreEntity) => {
      let prev = get().getCoreDataById(coreEntity.id)
      const db = novelCoreDb(coreEntity.type)
      if (!db) {
        notification.error({ message: '数据创建失败' })
        return Promise.resolve({ code: 400, message: '数据创建失败' })
      }
      return db.addData(coreEntity as any).then((res) => {
        if (res.code === 200) {
          if (prev) {
            set((state) => ({
              ...state,
              [coreEntity.type]: [
                ...state[coreEntity.type as CoreEntityType].filter(coreEntity => coreEntity.id !== prev?.id),
                coreEntity
              ]
            }))
          } else {
            set((state) => ({
              ...state,
              [coreEntity.type]: [...state[coreEntity.type as CoreEntityType], coreEntity]
            }))
          }


          if ('project' in coreEntity.relationships
            && coreEntity.relationships.project
            && coreEntity.relationships.project.type === 'novels') {
            get().structuralUpdateCoreData({
              id: coreEntity.relationships.project.id,
              meta: {
                updatedAt: new Date().toISOString()
              }
            })
          }

        } else {
          notification.error({ message: res.message })
        }
        return res
      })
    },
    updateCoreData: (nextEntity, skipDbUpdate) => {
      let prev = get().getCoreDataById(nextEntity.id)
      if (!prev) {
        notification.error({ message: '数据不存在' })
        return Promise.resolve({ code: 400, message: '数据不存在' })
      }
      prev = nextEntity
      // 更新时间
      prev.meta.updatedAt = new Date().toISOString()

      set((state) => ({
        ...state,
        [prev.type]: state[prev.type as CoreEntityType].map(coreEntity =>
          coreEntity.id === prev.id
            ? prev
            : coreEntity)
      }))
      const db = novelCoreDb(prev.type)
      // 跳过数据库更新或数据库不存在，直接返回
      if (skipDbUpdate || !db) return Promise.resolve({ code: 200 })
      return db.updateData(prev as any).then((res) => {
        if (res.code !== 200) {
          notification.error({ message: res.message })
        }

        if ('project' in prev.relationships && prev.relationships.project && prev.relationships.project.type === 'novels') {
          get().structuralUpdateCoreData({
            id: prev.relationships.project.id,
            meta: {
              updatedAt: prev.meta.updatedAt
            }
          })
        }

        return res
      })
    },
    structuralUpdateCoreData: ({ id, ...updates }, skipDbUpdate) => {
      let prev = get().getCoreDataById(id)
      if (!prev) {
        notification.error({ message: '数据不存在' })
        return Promise.resolve({ code: 400, message: '数据不存在' })
      } else {
        prev = structuralUpdate(prev, updates as Partial<CoreEntity>)
      }
      return get().updateCoreData(prev, skipDbUpdate)
    },
    deleteCoreData: (id: string) => {
      let prev = get().getCoreDataById(id)
      if (!prev) return Promise.resolve({ code: 200 })
      const db = novelCoreDb(prev.type)
      if (!db) {
        notification.error({ message: '数据不存在' })
        return Promise.resolve({ code: 400, message: '数据不存在' })
      }
      return db.deleteData(id).then((res) => {
        if (res.code === 200) {
          set((state) => ({ ...state, [prev.type]: state[prev.type as CoreEntityType].filter(coreEntity => coreEntity.id !== id) }))
        } else {
          notification.error({ message: res.message })
        }
        return res
      })
    },
    getCoreDataById: <T extends CoreEntityType>(id: string, type?: T) => {
      const state = get()
      if (type) {
        return state[type].find(coreEntity => coreEntity.id === id) as CoreEntityMap<T> | undefined
      }
      for (const type of Object.values(CoreEntityType)) {
        const found = state[type].find(coreEntity => coreEntity.id === id)
        if (found) return found as CoreEntityMap<T> | undefined
      }
      return undefined
    },
    getCoreDataByType: <T extends CoreEntityType>(type: T) => {
      return get()[type] as CoreEntityMap<T>[]
    },
    getCoreData: () => {
      const state = get()
      let result: CoreEntity[] = []
      for (const type of Object.values(CoreEntityType)) {
        result.push(...(state[type] as CoreEntity[]))
      }
      return result
    },
    initNovelDetailsById: async (novelId: string) => {
      const result: {
        novelActs: CoreEntityMap<CoreEntityType.NovelActs>[],
        novelChapters: CoreEntityMap<CoreEntityType.NovelChapters>[],
        novelScenes: CoreEntityMap<CoreEntityType.NovelScenes>[],
        codexEntries: CoreEntityMap<CoreEntityType.CodexEntries>[],
        codexFields: CoreEntityMap<CoreEntityType.CodexFields>[],
        codexCategories: CoreEntityMap<CoreEntityType.CodexCategories>[],
        snippets: CoreEntityMap<CoreEntityType.Snippets>[],
        chatThreads: CoreEntityMap<CoreEntityType.ChatThreads>[],
        chatMessages: CoreEntityMap<CoreEntityType.ChatMessages>[],
      } = {
        novelActs: [],
        novelChapters: [],
        novelScenes: [],
        codexEntries: [],
        codexFields: [],
        codexCategories: [],
        snippets: [],
        chatThreads: [],
        chatMessages: [],
      }
      const { data: novelDetail } = await databaseManager.novels?.getDataById(novelId)
      if (!novelDetail) return Promise.reject({ code: 400, message: '未找到小说' })

      await Promise.all(
        [
          databaseManager.novelActs?.getAllData().then(res => {
            result.novelActs = res.data?.filter(
              item => novelDetail.relationships.acts.includes(item.id)
            ) || []
          }),
          databaseManager.codexEntries.getAllData().then(res => {
            result.codexEntries = res.data?.filter(
              item => item.relationships.project.id === novelDetail.id
            ) || []
          }),
          databaseManager.codexFields.getAllData().then(res => {
            result.codexFields = res.data?.filter(
              item => item.relationships.project.id === novelDetail.id
            ) || []
          }),
          databaseManager.codexCategories.getAllData().then(res => {
            result.codexCategories = res.data?.filter(
              item => item.relationships.project.id === novelDetail.id
            ) || []
          }),
          databaseManager.novelChapters.getAllData().then(res => {
            result.novelChapters = res.data?.filter(
              item => item.relationships.project.id === novelDetail.id
            ) || []
          }),
          databaseManager.novelScenes.getAllData().then(res => {
            result.novelScenes = res.data?.filter(
              item => item.relationships.project.id === novelDetail.id
            ) || []
          }),
          databaseManager.snippets.getAllData().then(res => {
            result.snippets = res.data?.filter(
              item => item.relationships.project.id === novelDetail.id
            ) || []
          }),
          databaseManager.chatThreads.getAllData().then(res => {
            result.chatThreads = res.data?.filter(
              item => item.relationships.project.id === novelDetail.id
            ) || []
          })
        ]
      )

      const threadIds = result.chatThreads.map(thread => thread.id)
      databaseManager.chatMessages.getAllData().then(res => {
        result.chatMessages = res.data?.filter(
          item => threadIds.includes(item.relationships.thread.id)
        ) || []
      })

      set((state) => ({
        ...state,
        ...result,
        currentNovelId: novelId,
      }))
      return Promise.resolve({ code: 200 })
    },

    initCoreData: () => {
      const types = [
        CoreEntityType.Novels,
        CoreEntityType.NovelSeries,
        CoreEntityType.Prompts,
        CoreEntityType.PromptDefaults,
        CoreEntityType.PromptPersonas,
        CoreEntityType.ModelBanks,
        CoreEntityType.ModelBankLinks
      ]
      return Promise.all(
        types.map(type => {
          const db = novelCoreDb(type)
          if (!db) return Promise.reject({ code: 400, message: '数据库链接失败' })
          return db.getAllData().then((res) => {
            if (res.code !== 200) return Promise.reject(new Error('数据初始化失败'))
            return { type, data: res.data, code: res.code }
          })
        })
      ).then((res) => {
        if (res.some(item => item.code !== 200)) {
          notification.error({ message: '数据初始化失败' })
          return Promise.reject(new Error('数据初始化失败'))
        }
        res.forEach((item) => {
          if (item.data) {
            set((state) => ({ ...state, [item.type]: [...state[item.type], ...item.data!] }))
          }
        })
        return Promise.resolve({ code: 200 })
      }).catch((e) => {
        notification.error({ message: '数据初始化失败' })
        return Promise.reject(e)
      })
    }
  })
)

export const usePromptLibraryState = () => {
  const { prompts, promptDefaults, promptPersonas, promptPresets, modelBanks, modelBankLinks } = useNovelCoreStore();
  const promptLibraryState: PromptLibraryState[] = useMemo(() => {
    return [...prompts, ...promptDefaults, ...promptPersonas, ...promptPresets, ...modelBanks, ...modelBankLinks];
  }, [prompts, promptDefaults, promptPersonas, promptPresets, modelBanks, modelBankLinks]);
  return promptLibraryState;
}

export const usePromptsByType = (type: PromptType): CoreEntityMap<CoreEntityType.Prompts>[] => {
  const { prompts } = useNovelCoreStore();
  const promptsByType: CoreEntityMap<CoreEntityType.Prompts>[] = useMemo(() => {
    return prompts.filter(prompt => {
      return prompt.attributes.type === type;
    });
  }, [prompts, type]);
  return promptsByType;
}

export function useNovelCoreFormData<T extends CoreEntityType>(type: T, id?: string | null | undefined): {
  formData: CoreEntityMap<T> | undefined,
  realData: CoreEntityMap<T> | undefined,
  /** 部分更新 */
  onFormChange: (data: DeepPartial<CoreEntityMap<T>>) => void,
  /** 全量更新 */
  onFormFullChange: (data: CoreEntityMap<T>) => void,
  /** 提交 */
  onFormSubmit: (data?: DeepPartial<CoreEntityMap<T>>) => void
} {
  const novelCoreStore = useNovelCoreStore();
  const listByType = novelCoreStore[type] as CoreEntityMap<T>[];
  const { updateCoreData, addCoreData, structuralUpdateCoreData } = novelCoreStore;
  const [formData, setFormData] = useState<CoreEntityMap<T> | undefined>(undefined);
  // 是否正在编辑
  const [isEditing, setIsEditing] = useState(false);

  const realData = useMemo<CoreEntityMap<T> | undefined>(() => {
    if (!id) return undefined;
    return listByType.find(data => data.id === id);
  }, [id, listByType]);

  useEffect(() => {
    if (!realData) return;
    if (!formData || formData.id !== realData.id || !isEditing) {
      setFormData(realData);
    } else {
      setFormData(structuralUpdate(formData, realData));
    }
  }, [id, realData]);

  const onFormChange = useCallback((data: DeepPartial<CoreEntityMap<T>>) => {
    if (!id || !formData) return;
    setIsEditing(true);
    setFormData(structuralUpdate(formData, data));
  }, [id, formData]);

  const onFormFullChange = useCallback((data: CoreEntityMap<T>) => {
    if (!id || !formData) return;
    setIsEditing(true);
    setFormData(data);
  }, [id, formData]);

  const onFormSubmit = useCallback((data?: DeepPartial<CoreEntityMap<T>>) => {
    if (!id || !formData) return;
    if (!isEditing && !data) return;
    setIsEditing(false);
    if (formData.meta.state === 'draft') {
      const next = structuralUpdate(formData, { ...data, meta: { state: 'active' } }) as CoreEntity;
      addCoreData(next);
    } else if (data) {
      structuralUpdateCoreData({ id, ...data });
    } else {
      updateCoreData(formData);
    }
  }, [id, formData, addCoreData, updateCoreData, structuralUpdateCoreData]);

  return { formData, realData, onFormChange, onFormFullChange, onFormSubmit };
}


export const useCoreDataById = <T extends CoreEntityType>(id: string, type: T) => {
  const { getCoreDataById } = useNovelCoreStore();
  return getCoreDataById(id, type) as CoreEntityMap<T> | undefined;
}


/**
 * 按类别分组条目
 * 
 * 该方法将代码库条目按照两种方式进行分组：
 * 1. 自定义类别分组：基于用户创建的代码库类别，根据条目的类型、字段值和标签进行匹配
 * 2. 基础类型分组：按照条目的基础类型（角色、地点、物品等）进行分组
 * 
 * 分组逻辑：
 * - 自定义类别：使用 getCodexEntryIsInCategory 方法判断条目是否属于某个类别
 * - 基础类型：直接根据条目的 attributes.type 进行匹配
 * 
 * @returns 返回分组后的条目数组，每个分组包含：
 *   - label: 分组显示名称
 *   - type: 条目类型（仅基础分组有）
 *   - icon: 分组图标组件
 *   - category: 类别对象（仅自定义类别有）
 *   - children: 该分组下的条目数组
 */
export const useCodexEntryGroups = () => {
  const { codexEntries, codexCategories } = useNovelCoreStore();

  const codexEntryGroups = useMemo(() => {
    return getCodexEntryGroups(codexEntries, codexCategories);
  }, [codexEntries, codexCategories]);

  return codexEntryGroups;
}

// 按类型获取选中的提示词 没有侧用系统提示词
export const useSelectedPromptByType = (type: PromptType, promptId?: string) => {
  const { prompts } = useNovelCoreStore();

  const result = useMemo(() => {
    let prompt = (promptId || defaultsState.defaultPromptsMap[type]) ? prompts.find(prompt => prompt.id === (promptId || defaultsState.defaultPromptsMap[type])) : undefined;
    if (!prompt && !promptId && defaultsState.defaultPromptsMap[type]) {
      prompt = prompts.find(prompt => prompt.id === defaultsState.defaultPromptsMap[type]);
    }
    return prompt;
  }, [prompts, promptId, type]);

  return result;
}

// 按类型获取提示词以及提示词下模型分组
export const usePromptModelGroupsByType = (type: PromptType) => {
  const { prompts, modelBankLinks, modelBanks } = useNovelCoreStore();

  const result = useMemo(() => {
    return prompts.filter(prompt => prompt.attributes.type === type).map(prompt => {
      const linkIds = modelBankLinks.filter(modelBankLink => modelBankLink.relationships.prompt.id === prompt.id)
        .map(modelBankLink => modelBankLink.relationships.modelBank.id);
      return {
        label: prompt.attributes.name,
        key: prompt.id,
        isSystem: prompt.relationships.owner.id === 'system',
        children: [{
          label: '其它',
          key: 'other',
          children: prompt.attributes.models,
        }].concat(
          modelBanks.filter(bank => linkIds.includes(bank.id)).map(bank => ({
            label: bank.attributes.name,
            key: bank.id,
            children: bank.attributes.models,
          }))
        ).filter(group => group.children.length),
      }
    });
  }, [modelBankLinks, modelBanks]);

  return result;
}

export const useNovelActsTree = () => {
  const { novelActs, novelChapters, novelScenes, currentNovelId } = useNovelCoreStore();
  return getNovelActsTree(currentNovelId, novelActs, novelChapters, novelScenes);
}